import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Radio,
  Stack
} from '@chakra-ui/react'
import type { AxiosError, AxiosResponse } from 'axios'
import {
  DateFormControl,
  InputFormControl,
  RadioGroupFormControl,
  SelectFormControl,
  SwitchFormControl,
} from 'BrokerTsLib/src/components'
import type {
  AppInfoMessage
} from 'BrokerTsLib/src/dtos'
import {
  AppInfoMessageImportanceTypes,
} from 'BrokerTsLib/src/dtos'
import { getProductName } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { AppInfoMessageValidationSchema } from 'BrokerTsLib/src/validation/appInfoMessage'
import { forwardRef, useMemo } from 'react'

import { FormSection } from '@/containers/forms/FormSection'
import { useInsurers } from '@/hooks'

import { useAvailableProducts } from '@/hooks/useProducts'
import { FormGroup } from '../FormGroup'
import { TextEditor } from '../TextEditor'

export const AppInfoForm = forwardRef(function NewsForm(
  {
    appInfo,
    onSubmit,
  }: {
    appInfo: AppInfoMessage
    onSubmit: (value: AppInfoMessage) => Promise<AxiosResponse | AxiosError>
  },
  ref,
) {
  const methods = useForm<AppInfoMessage>({
    resolver: yupResolver(AppInfoMessageValidationSchema),
    mode: 'onBlur',
    defaultValues: { ...appInfo },
  })
  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors, isSubmitting },
    control,
  } = methods
  const selectedProduct = watch('insuranceCategoryType')
  const availableProducts = useAvailableProducts()
  const { data: insurersData } = useInsurers(
    false,
    false,
    undefined,
    selectedProduct
  )

  const types = useMemo(
    () => [
      ...(insurersData?.data?.map((x) => x.name) ?? []),
      'ČKP',
      'ARES',
      'CebiCAT',
      'CebiGlass',
      'VinOnline',
    ],
    [insurersData],
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={8}>
          <FormSection ref={ref} sectionTitle="Obsah" wide>
            <Stack gap={4}>
              <FormGroup alignItems={'start'}>
                <DateFormControl name="messageDate" w={40}
                  label="Datum"/>

                <InputFormControl
                  control={control}
                  name="messageHeader"
                  label="Titulek"
                  isRequired
                  w="full"
                />
              </FormGroup>

              <FormControl isInvalid={!!errors.messageText}>
                <FormLabel>Text</FormLabel>
                <TextEditor
                  minHeight='200px'
                  value={getValues('messageText') ?? ''}
                  onBlur={(newValue) => setValue('messageText', newValue)}
                />
              </FormControl>
            </Stack>
          </FormSection>
          <FormSection sectionTitle="Nastavení" wide>
            <Stack gap={4}>
              {!appInfo.insuranceCategoryType && (
                <SelectFormControl isRequired name='insuranceCategoryType'
                  label="Produkt"
                  options={availableProducts.map((product) => ({
                    label: getProductName(product) ?? '',
                    value: product.toString()
                  }))}
                />
              )}
              <SwitchFormControl
                whiteSpace="nowrap"
                alignSelf="start"
                control={control}
                name="messageShow"
                label="Zobrazovat"
              />

              <RadioGroupFormControl
                name="messageImportance"
                control={control}
                label="Důležitost"
                valueAsNumber
              >
                <Stack direction="row" gap={6}>
                  <Radio
                    variant="color"
                    colorScheme="blue"
                    value={AppInfoMessageImportanceTypes.Standard.toString()}
                    gap={3}
                  >
                    Standardní
                  </Radio>

                  <Radio
                    variant="color"
                    colorScheme="orange"
                    value={AppInfoMessageImportanceTypes.High.toString()}
                    gap={3}
                  >
                    Vysoká
                  </Radio>

                  <Radio
                    variant="color"
                    colorScheme="red"
                    value={AppInfoMessageImportanceTypes.Unavailability.toString()}
                    gap={3}
                  >
                    Nedostupnost
                  </Radio>
                </Stack>
              </RadioGroupFormControl>
              <SelectFormControl name='type' control={control}
                label="Typ"
                options={types.map(type => ({
                  label: type,
                  value: type
                }))}
                unselectedText='Vyberte typ (volitelné)'
              />
            </Stack>
          </FormSection>

          <Flex justifyContent="right">
            <Button px={12} type="submit" isLoading={isSubmitting}>
              Uložit
            </Button>
          </Flex>
        </Stack>
      </form>
    </FormProvider>
  )
})
